/**
 * Canned sayings: the catalogue every player starts with, the bounds a custom one is held to,
 * and the one function that turns an id into text.
 *
 * **A SAYING IS AN ID ON THE WIRE, NEVER FREE TEXT.** That is the whole security design of the
 * feature and everything in this file exists to serve it: a client says which saying it wants,
 * the server resolves the words, and there is no path by which a browser can put a string of its
 * own choosing on another player's screen. Free text would be a chat — a moderation surface, a
 * spam surface and a harassment surface — and this is deliberately not one.
 *
 * The catalogue is shared rather than server-local for the ordinary reason: the picker draws
 * from this list, the simulation resolves against it, and two copies would let a client offer a
 * saying the server refuses. It is also what a *worker* resolves with, which matters more than
 * it looks — a worker has no database driver, so the defaults have to be knowable from the build
 * alone. Only a player's approved *custom* sayings cannot be, and those ride the signed ticket
 * (see `WsTicket.sayings`).
 *
 * Ids are stable and never reused. A default's id is a slug and a custom one's is the
 * `user_sayings.id` uuid it was submitted under, so the two can never collide and a resolver can
 * try the catalogue first without a prefix or a namespace byte.
 */

import { TICK_RATE_HZ } from './simulation.js';

/** One saying: the id that travels, and the words that do not. */
export interface Saying {
  /** A catalogue slug, or the uuid of an approved `user_sayings` row. */
  id: string;
  text: string;
}

/**
 * The list every player starts with — 32 of them, family-friendly, and about *this* game rather
 * than about each other.
 *
 * Curated rather than generated, and short rather than exhaustive: a picker is a scroll list
 * under a thumb, so the useful length is the one a player can find something in during a chase.
 * Everything here is something you would say to somebody who just ate you or just got away, which
 * is the only conversation this game actually has.
 */
export const DEFAULT_SAYINGS = [
  { id: 'gg', text: 'Good game!' },
  { id: 'nice-one', text: 'Nice one!' },
  { id: 'well-played', text: 'Well played.' },
  { id: 'oops', text: 'Oops.' },
  { id: 'too-big', text: "You're too big for me." },
  { id: 'come-here', text: 'Come here, little dot.' },
  { id: 'run', text: 'Run!' },
  { id: 'no-u', text: 'No u.' },
  { id: 'hungry', text: "I'm starving over here." },
  { id: 'snack', text: 'You look like a snack.' },
  { id: 'not-today', text: 'Not today!' },
  { id: 'so-close', text: 'So close!' },
  { id: 'help', text: 'Help!' },
  { id: 'follow-me', text: 'Follow me!' },
  { id: 'truce', text: 'Truce?' },
  { id: 'betrayed', text: 'I trusted you.' },
  { id: 'nom', text: 'Nom nom nom.' },
  { id: 'laggy', text: 'That was lag, obviously.' },
  { id: 'mind-the-spike', text: 'Mind the spike!' },
  { id: 'behind-you', text: 'Behind you!' },
  { id: 'team-up', text: 'Team up?' },
  { id: 'sorry-not-sorry', text: 'Sorry. Not sorry.' },
  { id: 'bye', text: 'Bye bye.' },
  { id: 'wow', text: 'Wow.' },
  { id: 'brb', text: 'Back in a sec.' },
  { id: 'top-dog', text: 'Top dog coming through.' },
  { id: 'tiny', text: "I'm just a tiny dot." },
  { id: 'rematch', text: 'Rematch!' },
  { id: 'regret', text: 'I regret everything.' },
  { id: 'on-purpose', text: 'That was on purpose.' },
  { id: 'stop-it', text: 'Stop chasing me!' },
  { id: 'thanks-snack', text: 'Thanks for the snack.' },
] as const satisfies readonly Saying[];

export type DefaultSayingId = (typeof DEFAULT_SAYINGS)[number]['id'];

/** Catalogue lookup. Built once — a saying is resolved on the tick a player presses the button. */
const DEFAULTS_BY_ID: ReadonlyMap<string, Saying> = new Map(
  DEFAULT_SAYINGS.map((saying) => [saying.id, saying]),
);

/** The catalogue entry an id names, or undefined for an id this build does not know. */
export function defaultSaying(id: string): Saying | undefined {
  return DEFAULTS_BY_ID.get(id);
}

/**
 * The words an id resolves to for one speaker, or null when it resolves to nothing.
 *
 * **THE ONE RESOLVER**, and the only place the two halves of a player's vocabulary are put
 * together. The catalogue is tried first and a custom entry can never shadow one, which is not
 * only a precedence rule but a *containment* rule: an approved submission cannot be made to
 * replace the words behind `gg` for everybody who has it.
 *
 * `custom` is the speaker's own approved list — the ticket's `sayings` claim in the simulation,
 * and the database's answer everywhere else. `undefined` means this player may not speak at all
 * (a guest, or a bot), which is why an absent list is not the same as an empty one: an empty
 * list still resolves the defaults.
 */
export function resolveSayingText(id: string, custom?: readonly Saying[]): string | null {
  if (custom === undefined) return null;
  const fromCatalogue = DEFAULTS_BY_ID.get(id);
  if (fromCatalogue !== undefined) return fromCatalogue.text;
  for (const saying of custom) {
    if (saying.id === id) return saying.text;
  }
  return null;
}

/**
 * Longest id the wire will carry. A catalogue slug is a couple of dozen characters and a custom
 * id is a 36-character uuid, so this is headroom rather than a budget anything runs up against —
 * and a ceiling, so a forged frame cannot make the resolver walk a megabyte of "id".
 */
export const SAYING_ID_MAX_LENGTH = 64;

/**
 * How long a saying may be.
 *
 * Short on purpose, and the shortness is the moderation policy rather than a storage decision: a
 * bubble is read at a glance over somebody's blob while both of you are moving, and a submission
 * queue is a human reading sentences one at a time. Sixty characters is a line; a paragraph is a
 * chat message, which this is not.
 */
export const SAYING_TEXT_MIN_LENGTH = 2;
export const SAYING_TEXT_MAX_LENGTH = 60;

/**
 * How long a bubble stays on screen, and how often a player may raise one.
 *
 * Tick counts rather than timestamps, for the reason every other duration in the simulation is:
 * a tick count replays identically from a seed and a wall clock does not.
 *
 * **The cooldown is the rate limit, and it is the real one.** MAX_INPUTS_PER_TICK already bounds
 * a client to one input frame a tick, so without this a held button would be twenty sayings a
 * second — which is a chat, and a spam channel. Three seconds is long enough that a bubble has
 * come and (nearly) gone before the next may be raised, so a player cannot stack two on one
 * blob, and short enough that answering somebody does not feel like waiting your turn.
 */
export const SAYING_BUBBLE_SECONDS = 4;
export const SAYING_BUBBLE_TICKS = SAYING_BUBBLE_SECONDS * TICK_RATE_HZ;
export const SAYING_COOLDOWN_SECONDS = 3;
export const SAYING_COOLDOWN_TICKS = SAYING_COOLDOWN_SECONDS * TICK_RATE_HZ;

/**
 * How many approved custom sayings one account may hold.
 *
 * **This number is a ticket budget, not a taste judgement.** Approved customs ride the signed
 * WebSocket ticket, because the process that hosts a room has no database driver and cannot look
 * one up; the ticket is capped at 4096 characters by `clientHelloSchema`, and it already carries
 * a skin URL of up to MAX_SKIN_URL_LENGTH. Eight entries of SAYING_TEXT_MAX_LENGTH plus their
 * uuids is under a kilobyte of JSON, which leaves the ticket comfortably inside its ceiling with
 * the skin on it as well. Raising this without re-checking that arithmetic is how a player with a
 * long photo URL becomes a player who cannot join a match.
 */
export const MAX_CUSTOM_SAYINGS_PER_USER = 8;

/**
 * How many submissions one account may have waiting on a moderator at once.
 *
 * The queue is read by people. A cap per submitter is what stops one account being the whole
 * queue, and it is deliberately small: a player with three sentences in the queue is a player who
 * should wait for a verdict rather than keep typing.
 */
export const MAX_PENDING_SAYINGS_PER_USER = 3;

/**
 * A personal list must keep at least one saying, and cannot hold more than the whole vocabulary.
 *
 * The floor is not a formality. An empty list is indistinguishable from a list nobody has ever
 * edited — both are "no rows" — and a picker with nothing in it is a button that does nothing, so
 * the empty list is refused rather than given a second column to mean it.
 */
export const MIN_SAYING_LIST_LENGTH = 1;
export const MAX_SAYING_LIST_LENGTH = DEFAULT_SAYINGS.length + MAX_CUSTOM_SAYINGS_PER_USER;

/**
 * What a submission can be, in the order it moves through them.
 *
 * `pending` is where every row starts, and the other two are terminal: a moderator's verdict is
 * an UPDATE of the row that was already there, never a delete and never a second row, exactly as
 * lifting a ban is. `rejected` is kept for the same reason a lifted ban is kept — a queue that
 * could not answer "what happened to the one I sent" is a queue players re-submit into.
 */
export const SAYING_STATUSES = ['pending', 'approved', 'rejected'] as const;

export type SayingStatus = (typeof SAYING_STATUSES)[number];

/** The two a moderator may move a pending submission to. */
export const SAYING_VERDICTS = ['approved', 'rejected'] as const satisfies readonly SayingStatus[];

export type SayingVerdict = (typeof SAYING_VERDICTS)[number];
